import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { useGetAuthData, useGetIntegrations } from "../lib/hooks"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"
import React, { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog"
import { integrationRoute } from "@/lib/router"
import { client } from "@/lib/ts-rest"
import { queryClient } from "@/lib/react-query"
import { IntegrationLocation } from "./integration-sections/select-location"
import { SetUpDevices } from "./integration-sections/set-up-devices"
import { Timetable } from "./integration-sections/timetable"
import { UpdatePrayerTimeConfiguration } from "./integration-sections/update-prayer-time-configuration"
import { AthanNotificationSettings } from "./integration-sections/athan-notification-settings"
import { Payment } from "./integration-sections/payment"
import { validateClientProperty } from "@/lib/ts-rest"
import { ArrowLeftIcon } from "@heroicons/react/16/solid"
import { useMediaQuery } from "react-responsive"
import { ConnectAccounts } from "./integration-sections/connect-accounts"
import { trackEvent } from "@/lib/log"
import { useDashboard } from "@/lib/dashboard-context"

const errorToStep: Record<string, number> = {
  noLocation: 1,
  noAlexaAccount: 3,
  setupIncomplete: 4,
  notPaying: 5,
}

export function Integration() {
  const { data: integrations, refetch: refetchIntegrations } =
    useGetIntegrations()
  const { user } = useGetAuthData()
  const params = useParams({ from: integrationRoute.id })
  const integrationId = Number(params.integrationId)
  const query = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >
  const navigate = useNavigate()
  const { toast } = useToast()
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" })

  const initialStep = query.step ? Number(query.step) : undefined
  const [currentStep, setCurrentStep] = useState<number>(initialStep ?? 1)

  const { mutate: deleteIntegration } = client.deleteIntegration.useMutation({
    onSuccess: () => {
      trackEvent("deleted an integration", { integrationId })
      navigate({ to: "/" })
      queryClient.invalidateQueries({
        queryKey: [validateClientProperty("getIntegrations")],
      })
      toast({ title: "Connection deleted." })
    },
  })

  const integration = integrations?.body.integrations.find(
    (item) => item.integration.id === integrationId,
  )

  const [showDeletePresetDialog, setShowDeletePresetDialog] = useState(false)

  const hasAutoNavigated = useRef(false)
  useEffect(() => {
    if (integration && !initialStep && !hasAutoNavigated.current) {
      const errorId = integration.status.errorId
      if (errorId && errorToStep[errorId]) {
        hasAutoNavigated.current = true
        setCurrentStep(errorToStep[errorId])
      }
    }
  }, [integration, initialStep])

  const stepRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const hasScrolledToStep = useRef(false)

  useEffect(() => {
    if (!isMobile && initialStep && !hasScrolledToStep.current) {
      const el = stepRefs.current[Number(initialStep)]
      if (el) {
        hasScrolledToStep.current = true
        el.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }, [initialStep, isMobile, integration])

  const { setFlushContent } = useDashboard()

  useEffect(() => {
    if (isMobile) {
      setFlushContent(true)
      return () => setFlushContent(false)
    }
  }, [isMobile, setFlushContent])

  const hasLocation = Boolean(integration?.locationName)
  const hasAlexaAccount = Boolean(integration?.integration.alexaAccount)
  const isSetupComplete = Boolean(integration?.integration.isSetupComplete)
  const hasPaid = Boolean(user?.hasSpeakerSubscription)

  const steps: {
    step: number
    title: string
    subtitle: string
    Component: React.ReactNode
    isUnlocked: boolean
  }[] = [
    {
      step: 1,
      title: "Select location",
      subtitle: "Your location is required to calculate your prayer times.",
      Component: <IntegrationLocation integrationId={integrationId} />,
      isUnlocked: true,
    },
    integration?.integration.prayerTimeSource === "Mosque"
      ? {
          step: 2,
          title: "Preview prayer times",
          subtitle: `Preview the times for ${integration.locationName}.`,
          Component: <Timetable integrationId={integrationId} />,
          isUnlocked: hasLocation,
        }
      : integration?.integration.prayerTimeSource ===
            "PrayerTimeConfiguration" &&
          integration?.integration.prayerTimeConfiguration
        ? {
            step: 2,
            title: "Adjust prayer times",
            subtitle: `The following settings help determine the prayer timings for ${integration?.locationName}.`,
            Component: (
              <UpdatePrayerTimeConfiguration
                prayerTimeConfiguration={
                  integration?.integration.prayerTimeConfiguration as any
                }
              />
            ),
            isUnlocked: hasLocation,
          }
        : {
            step: 2,
            title: "Adjust prayer times",
            subtitle:
              "The following settings help determine the prayer timings for your location.",
            Component: null,
            isUnlocked: hasLocation,
          },
    {
      step: 3,
      title: "Log in to Amazon",
      subtitle: "Connect your Amazon account to enable the Pillars Skill",
      Component: <ConnectAccounts integrationId={integrationId} />,
      isUnlocked: hasLocation,
    },
    {
      step: 4,
      title: "Set up automation",
      subtitle: "Set up an Alexa Routine so the athan plays automatically.",
      Component: <SetUpDevices integrationId={integrationId} />,
      isUnlocked: hasAlexaAccount,
    },
    {
      step: 5,
      title: "Payment",
      subtitle: "A subscription is required to activate automatic athan.",
      Component: <Payment />,
      isUnlocked: isSetupComplete,
    },
    {
      step: 6,
      title: "Athan settings",
      subtitle: "You can enable or disable specific athans.",
      Component: <AthanNotificationSettings integrationId={integrationId} />,
      isUnlocked: hasPaid && hasAlexaAccount && isSetupComplete,
    },
  ]

  if (!integration) {
    return null
  }

  const currentStepData = steps.find((s) => s.step === currentStep)
  const currentStepIndex = steps.findIndex((s) => s.step === currentStep)
  const hasPrevious = currentStepIndex > 0
  const hasNext = currentStepIndex < steps.length - 1

  const isLastStep = currentStepIndex === steps.length - 1
  const nextStepData = hasNext ? steps[currentStepIndex + 1] : null
  const isNextStepUnlocked = nextStepData?.isUnlocked ?? false

  return (
    <>
      {!isMobile && (
        <>
          <div className="mb-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div className="text-primary font-medium md:text-3xl text-2xl">
              Set up Pillars with Alexa
            </div>
            <img src="/amazon-alexa.svg" className="h-7" />
          </div>
          <div className="mb-10">
            Set up the Pillars platform with your Amazon Alexa to hear the athan
            throughout your home!
          </div>
        </>
      )}
      {isMobile ? (
        <div className="flex flex-col h-full bg-white">
          {currentStepData && (
            <div
              className={cn(
                "flex-1 min-h-0 overflow-y-auto px-5 pt-5 pb-4",
                !currentStepData.isUnlocked &&
                  "opacity-50 cursor-not-allowed select-none",
              )}
            >
              <div
                className={cn(
                  "h-7 w-7 rounded-full flex justify-center items-center shrink-0 text-sm mb-2",
                  currentStepData.isUnlocked
                    ? "bg-primary text-white"
                    : "bg-gray-300 text-white",
                )}
              >
                {currentStepData.step}
              </div>
              <div
                className={cn(
                  "font-medium text-xl mb-2",
                  currentStepData.isUnlocked
                    ? "text-primary"
                    : "text-gray-400",
                )}
              >
                {currentStepData.title}
              </div>
              <div
                className={cn(
                  "text-sm",
                  currentStepData.isUnlocked ? "mb-5" : "text-gray-400",
                )}
              >
                {currentStepData.subtitle}
              </div>
              {currentStepData.isUnlocked && (
                <div className="mt-5">{currentStepData.Component}</div>
              )}
              {isLastStep && currentStepData.isUnlocked && (
                <div className="mt-10">
                  <Dialog
                    onOpenChange={setShowDeletePresetDialog}
                    open={showDeletePresetDialog}
                  >
                    <DialogTrigger asChild>
                      <Button variant="destructive">Delete Alexa connection</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Delete</DialogTitle>
                        <DialogDescription>
                          Are you sure you want to delete this connection?
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <div className="flex gap-3">
                          <DialogClose asChild>
                            <Button type="button" variant="secondary">
                              Cancel
                            </Button>
                          </DialogClose>
                          <Button
                            onClick={() =>
                              deleteIntegration({
                                params: { integrationId: String(integrationId) },
                                body: {},
                              })
                            }
                            variant="destructive"
                          >
                            Delete
                          </Button>
                        </div>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          )}
          <div className="border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex justify-between shrink-0">
            {hasPrevious ? (
              <Button
                variant="outline"
                onClick={() => {
                  const prevStep = steps[currentStepIndex - 1]
                  if (prevStep) setCurrentStep(prevStep.step)
                }}
              >
                Previous
              </Button>
            ) : (
              <div />
            )}
            {hasNext ? (
              <Button
                disabled={!isNextStepUnlocked}
                onClick={() => {
                  const nextStep = steps[currentStepIndex + 1]
                  if (nextStep) setCurrentStep(nextStep.step)
                }}
              >
                Next
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-10 mb-10">
          {steps.map(({ Component, step, subtitle, title, isUnlocked }) => (
            <div
              key={title}
              ref={(el) => {
                stepRefs.current[step] = el
              }}
              className={cn(
                "p-5 rounded-2xl bg-white",
                !isUnlocked && "opacity-50 cursor-not-allowed select-none",
              )}
            >
              <div className="flex gap-3 items-start">
                <div
                  className={cn(
                    "h-7 aspect-square rounded-full flex justify-center items-center shrink-0",
                    isUnlocked
                      ? "bg-primary text-white"
                      : "bg-gray-300 text-white",
                  )}
                >
                  {step}
                </div>
                <div className="flex-grow overflow-auto">
                  <div
                    className={cn(
                      "font-medium text-xl mb-2",
                      isUnlocked ? "text-primary" : "text-gray-400",
                    )}
                  >
                    {title}
                  </div>
                  <div
                    className={cn(
                      "text-sm",
                      isUnlocked ? "mb-5" : "text-gray-400",
                    )}
                  >
                    {subtitle}
                  </div>
                  {isUnlocked && <div className="mt-5">{Component}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isMobile && (
        <Dialog
          onOpenChange={setShowDeletePresetDialog}
          open={showDeletePresetDialog}
        >
          <DialogTrigger asChild>
            <Button variant="destructive">Delete Alexa connection</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this connection?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <div className="flex gap-3">
                <DialogClose asChild>
                  <Button type="button" variant="secondary">
                    Cancel
                  </Button>
                </DialogClose>

                <Button
                  onClick={() =>
                    deleteIntegration({
                      params: { integrationId: String(integrationId) },
                      body: {},
                    })
                  }
                  variant="destructive"
                >
                  Delete
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
